<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Header with dropdown menu -->
    <header class="bg-white shadow-sm px-4 md:px-8 py-4 relative">
      <div class="flex items-center justify-between">
        <!-- Menu dropdown button -->
        <button 
          @click="isMenuOpen = !isMenuOpen"
          class="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <font-awesome-icon 
            :icon="isMenuOpen ? 'times' : 'bars'" 
            class="w-5 h-5 text-slate-600"
          />
          <span class="text-sm font-medium text-slate-700">Menú</span>
        </button>
        
        <h1 class="text-xl md:text-2xl font-bold text-slate-800">Panel de Admin</h1>
        
        <!-- Spacer -->
        <div class="w-20"></div>
      </div>

      <!-- Dropdown Menu -->
      <div 
        v-if="isMenuOpen"
        class="absolute top-full left-0 right-0 bg-white shadow-lg border-t border-slate-200 z-50 max-h-[70vh] overflow-y-auto"
      >
        <nav class="p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <!-- Products Section -->
            <div class="space-y-3">
              <h3 class="text-sm font-semibold text-slate-400 uppercase tracking-wide">
                Productos
              </h3>
              <div class="space-y-1">
                <button
                  @click="setActiveTab('products')"
                  :class="menuItemClass('products')"
                >
                  Gestión de Productos
                </button>
                <button
                  @click="setActiveTab('cost-analysis')"
                  :class="menuItemClass('cost-analysis')"
                >
                  Análisis de Costos
                </button>
              </div>
            </div>

            <!-- Updates Section -->
            <div class="space-y-3">
              <h3 class="text-sm font-semibold text-slate-400 uppercase tracking-wide">
                Actualizaciones
              </h3>
              <div class="space-y-1">
                <button
                  @click="setActiveTab('bulk-update')"
                  :class="menuItemClass('bulk-update')"
                >
                  Actualización Masiva
                </button>
                <button
                  @click="setActiveTab('manual-update')"
                  :class="menuItemClass('manual-update')"
                >
                  Actualización Manual
                </button>
                <button
                  @click="setActiveTab('bulk-import')"
                  :class="menuItemClass('bulk-import')"
                >
                  Importación Masiva
                </button>
              </div>
            </div>

            <!-- Configuration Section -->
            <div class="space-y-3">
              <h3 class="text-sm font-semibold text-slate-400 uppercase tracking-wide">
                Configuración
              </h3>
              <div class="space-y-1">
                <button
                  @click="setActiveTab('categories')"
                  :class="menuItemClass('categories')"
                >
                  Categorías
                </button>
                <button
                  @click="setActiveTab('laboratories')"
                  :class="menuItemClass('laboratories')"
                >
                  Laboratorios
                </button>
                <button
                  @click="setActiveTab('users')"
                  :class="menuItemClass('users')"
                >
                  Usuarios
                </button>
                <button
                  @click="setActiveTab('config')"
                  :class="menuItemClass('config')"
                >
                  Configuración
                </button>
              </div>
            </div>

            <!-- System Section -->
            <div class="space-y-3">
              <h3 class="text-sm font-semibold text-slate-400 uppercase tracking-wide">
                Sistema
              </h3>
              <div class="space-y-1">
                <button
                  @click="setActiveTab('cache')"
                  :class="menuItemClass('cache')"
                >
                  Caché
                </button>
                <button
                  @click="setActiveTab('reset')"
                  :class="menuItemClass('reset')"
                >
                  Blanquear DB
                </button>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </header>

    <!-- Overlay for mobile -->
    <div 
      v-if="isMenuOpen"
      class="fixed inset-0 bg-black bg-opacity-25 z-40"
      @click="isMenuOpen = false"
    ></div>

    <!-- Main Content -->
    <main class="p-4 md:p-8">
      <div class="max-w-full">
        <ProductForm v-if="activeTab === 'products'" />
        <CategoryManager v-if="activeTab === 'categories'" />
        <LaboratoryManager v-if="activeTab === 'laboratories'" />
        <UserManager v-if="activeTab === 'users'" />
        <BulkPriceUpdate v-if="activeTab === 'bulk-update'" />
        <ManualPriceUpdate v-if="activeTab === 'manual-update'" />
        <BulkImport v-if="activeTab === 'bulk-import'" />
        <CostAnalysis v-if="activeTab === 'cost-analysis'" />
        <ConfigPanel v-if="activeTab === 'config'" />
        <CacheManager v-if="activeTab === 'cache'" />
        <DatabaseReset v-if="activeTab === 'reset'" />
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import ProductForm from '../components/ProductForm.vue';
import LaboratoryManager from '../components/LaboratoryManager.vue';
import BulkPriceUpdate from '../components/BulkPriceUpdate.vue';
import CategoryManager from '../components/CategoryManager.vue';
import ManualPriceUpdate from '../components/ManualPriceUpdate.vue';
import ConfigPanel from '../components/ConfigPanel.vue';
import CacheManager from '../components/CacheManager.vue';
import DatabaseReset from '../components/DatabaseReset.vue';
import BulkImport from '../components/BulkImport.vue';
import UserManager from '../components/user/UserManager.vue';
import CostAnalysis from '../components/cost-analysis/CostAnalysis.vue';

const activeTab = ref('products');
const isMenuOpen = ref(false);

const setActiveTab = (tab: string) => {
  activeTab.value = tab;
  isMenuOpen.value = false; // Close menu after selection
};

const menuItemClass = computed(() => (tab: string) => {
  return [
    'w-full text-left px-3 py-2 rounded-lg transition-colors duration-200 text-sm font-medium',
    activeTab.value === tab
      ? 'bg-indigo-100 text-indigo-700'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
  ];
});

// Close menu when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('header') && isMenuOpen.value) {
    isMenuOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Custom scrollbar for dropdown */
nav::-webkit-scrollbar {
  width: 6px;
}

nav::-webkit-scrollbar-track {
  background: #f1f5f9;
}

nav::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

nav::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>